import { useEffect, useMemo, useState } from 'react';

import type { IPredicate, IPredicateGroup, Segment, Segments } from '@feathr/blackbox';
import type { TPersonStatus } from '@feathr/extender/components/PersonStatusSegmentedControl';
import { mergePersonStatusPredicate } from '@feathr/extender/components/PersonStatusSegmentedControl/PersonStatusSegmentedControl.utils';

interface IUsePersonStatusCountsArgs {
  segment: Segment;
  predicates: Array<IPredicate | IPredicateGroup>;
  keywords?: string;
  resetDependencies?: unknown;
}

interface IUsePersonStatusCountsResult {
  activeCount?: number;
  archivedCount?: number;
  isLoading: boolean;
}

export function usePersonStatusCounts({
  segment,
  predicates,
  keywords,
  resetDependencies,
}: IUsePersonStatusCountsArgs): IUsePersonStatusCountsResult {
  const [activeCount, setActiveCount] = useState<number | undefined>();
  const [archivedCount, setArchivedCount] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const segmentMode = segment.get('mode');
  const lookbackMode = segment.get('lookback_mode');
  const lookbackValue = segment.get('lookback_value');
  const parentSegment = segment.get('parent_segment');
  // Memoize predicates so effect dependencies stay stable and can reconnect list resets
  const predicatesJSON = useMemo(() => JSON.stringify(predicates ?? []), [predicates]);
  const parsedPredicates = useMemo(
    () =>
      predicatesJSON ? (JSON.parse(predicatesJSON) as Array<IPredicate | IPredicateGroup>) : [],
    [predicatesJSON],
  );

  useEffect(() => {
    const segmentsCollection = segment.collection as Segments | undefined;
    if (!segmentsCollection) {
      setActiveCount(undefined);
      setArchivedCount(undefined);
      setIsLoading(false);
      return;
    }

    // Base POST payload shared by both active/archived count requests
    const basePayload: Record<string, unknown> = {
      lookback_mode: lookbackMode,
      lookback_value: lookbackValue,
    };

    if (parentSegment) {
      basePayload['parent_segment'] = parentSegment;
    }

    if (keywords?.trim()) {
      basePayload['keywords'] = keywords.trim();
    }

    const controller = new AbortController();
    let isCancelled = false;

    const buildStatusPredicates = (status: TPersonStatus): Array<IPredicate | IPredicateGroup> => {
      return mergePersonStatusPredicate(parsedPredicates, status, segmentMode);
    };

    // Wrap count fetch so both requests share the same abort controller / payload
    const fetchCount = async (status: TPersonStatus): Promise<number> => {
      const payload = {
        ...basePayload,
        predicates: buildStatusPredicates(status),
        // When merging with person status, use match_all to ensure segment predicates
        // (with their own mode) are ANDed with is_archived
        mode: 'match_all',
      };

      return segmentsCollection.fetchCountWithPayload(payload, controller.signal);
    };

    // Kick off both count requests and cache the results for the segmented control
    const loadCounts = async (): Promise<void> => {
      setIsLoading(true);

      try {
        const [activeCountValue, archivedCountValue] = await Promise.all([
          fetchCount('active'),
          fetchCount('archived'),
        ]);

        if (!isCancelled) {
          setActiveCount(activeCountValue);
          setArchivedCount(archivedCountValue);
        }
      } catch (error) {
        if (controller.signal.aborted) {
          throw error;
        }

        if (!isCancelled) {
          setActiveCount(undefined);
          setArchivedCount(undefined);
        }

        // Re-throw so static analyzers know we do not swallow unexpected errors
        throw error;
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCounts().catch(() => undefined);

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [
    keywords,
    lookbackMode,
    lookbackValue,
    parentSegment,
    predicatesJSON,
    resetDependencies,
    segment,
    segmentMode,
  ]);

  return {
    activeCount,
    archivedCount,
    isLoading,
  };
}

export default usePersonStatusCounts;
